import { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import autoBind from 'react-autobind';
import axios from 'axios';
import { isMobileOnly } from 'react-device-detect';
import isNull from 'lodash-es/isNull';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';

import loadable from '../../../utils/loadable';

import withComponentName from '../../../decorators/withComponentName';
import StoryFilters from '../../StoryFilters';
import StoryCard from '../../TrendingStories/StoryCard';
import RecommendedStories from '../../RecommendedStories';
import Preloader from '../../Preloader';
import NewStoriesButton from '../../Buttons/NewStoriesButton';
import StoryCaughtUp from '../../StoryCaughtUp';
import Summarisation from '../../Summarisation';
import OpenFiltersButton from '../../StoryFilters/StoryFiltersButton/OpenFiltersButton';
import PiwikContext from '../../../services/piwikContext';

import * as storyActions from '../../../actions/stories.actions';
import * as searchActions from '../../../actions/searchActions';
import * as commonActions from '../../../actions/common.actions';
import * as preferencesActions from '../../../actions/preferencesActions';

import { resetTrendingFilters } from '../../../actions/storyFilters.action';
import {
  getTopicGroup,
  getFormattedQuery,
  compareFilters,
} from '../../../helpers/helpers';
import { getBrowserScrollTop, getParamValueFromUrl, pickParams } from '../../../helpers/commonHelpers';
import { filterSelectedTimeFilter } from '../../../helpers/timeFilterHelpers';
import { filterSelectedSentimentForStories } from '../../../helpers/storyHelpers';
import { buildAdvancedSearchQuery } from '../../../helpers/searchbarHelpers';

import { SILVER } from '../../../data/permissions';
import { STORIES_LIMIT } from '../../../data/filters/storyFilters';
import { ADVANCED_SEARCH, searchTypeListMapping } from '../../../data/searchbar/types';

import StoriesSvc from '../../../services/StoriesSvc';
import { storiesLoadAPIData } from '../../../services/dbServices/searchbarDbServices';

import Styles from './styles.module.scss';

const Masonry = loadable(() => import('react-masonry-component'));
const cx = classNames.bind(Styles);

export class SearchResult extends PureComponent {
  constructor(props) {
    super(props);
    autoBind(this);

    this.state = {
      scrollPosition: 0,
      initialStoriesLoaded: false,
      newStoriesAvailable: false,
      seeMoreStoriesTop: 0,
      position: 'absolute',
      pageToken: null,
      isWarningMessageShow: true,
    };

    this.isTabActive = false;
    this.newStoriesFetching = false;
    this.searchResultTimer = 0;
    this.isScrolling = 0;
    this.storyUpdateTimer = 0;
    this.storiesRef = createRef();

    this.cancelToken = axios.CancelToken;
    this.sourceCancelToken = this.cancelToken.source();
  }

  componentDidMount() {
    const { storyFilters, investorPage, searchQueryFromUrl } = this.props;
    this.isTabActive = true;
    resetTrendingFilters();

    if (investorPage && searchQueryFromUrl) {
      this.loadStoriesOnFilters();
    }
    if (storyFilters.refresh_rate !== 'never' && Number(storyFilters.refresh_rate)) {
      this.searchResultTimer = setTimeout(this.loadStoriesOnFilters, storyFilters.refresh_rate);
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  componentDidUpdate(prevProps) {
    const {
      storyFilters,
      searchActive,
      scrollUpPressed,
      actions,
      promobarHeight,
      nextPageToken,
    } = this.props;
    const { newStoriesAvailable, pageToken } = this.state;

    if (searchActive && pageToken !== nextPageToken) {
      this.setState({ pageToken: nextPageToken });
    }

    if (promobarHeight !== prevProps.promobarHeight && newStoriesAvailable) {
      this.onScroll();
    }

    if (
      !compareFilters(storyFilters, prevProps.storyFilters)
      || searchActive !== prevProps.searchActive
    ) {
      this.stopTimers();

      this.setState({
        pageToken: null,
        newStoriesAvailable: false,
      });
      this.loadStoriesOnFilters();
      this.scrollToStart(false);
    }

    if (scrollUpPressed) {
      actions.toggleScrollUpState(false);
      this.scrollToStart(false);
    }
  }

  componentWillUnmount() {
    this.stopTimers();
    clearTimeout(this.loadStoriesTimeout);
    clearTimeout(this.storyUpdateTimer);
    this.isTabActive = false;
    if (this.sourceCancelToken) this.sourceCancelToken.cancel();
    window.removeEventListener('scroll', this.onScroll);
    const { actions } = this.props;
    actions.resetStoriesReduxData();
    actions.toggleStoryFilters(false);
    clearTimeout(this.popularStoriesTimer);
    clearTimeout(this.searchResultTimer);
  }

  onScroll() {
    const {
      actions,
      freezeStories,
      promobarHeight,
      width,
    } = this.props;
    const { scrollPosition } = this.state;

    if (!this.storiesRef.current) return;

    const topOffset = width < 768 ? 330 : 345;
    const notFixed = topOffset - getBrowserScrollTop() > 15;
    this.setState({
      seeMoreStoriesTop: notFixed ? 0 : (15 + promobarHeight),
      position: notFixed ? 'absolute' : 'fixed',
      isFilterFixed: !notFixed,
    });

    clearTimeout(this.isScrolling);
    this.isScrolling = setTimeout(() => {
      const scrollTop = getBrowserScrollTop();
      if (Math.abs(scrollPosition - scrollTop) > 250 && freezeStories) {
        actions.freezeStories(false);
      }

      this.setState({
        scrollPosition: scrollTop,
      });
    }, 100);

    const docTop = this.storiesRef.current.offsetTop;
    const docHeight = this.storiesRef.current.offsetHeight;
    const winheight = window.innerHeight === undefined
      ? document.documentElement.clientHeight
      : window.innerHeight;
    const scrollpoint = window.scrollY === undefined
      ? window.document.documentElement.scrollTop
      : window.scrollY;

    if ((scrollpoint + winheight) >= (docHeight + docTop)) {
      this.onScrollLoadData();
    }
  }

  onFocus() {
    const { hasStoryError } = this.state;
    if (hasStoryError) {
      this.setState({
        pageToken: null,
        newStoriesAvailable: false,
      }, () => {
        const { storyFilters } = this.props;
        this.fetchStories(storyFilters);
      });
    }
  }

  onVisibilityChange() {
    if (document.hidden) {
      clearTimeout(this.popularStoriesTimer);
      if (this.sourceCancelToken) this.sourceCancelToken.cancel();
      this.isTabActive = false;
    } else {
      if (!this.sourceCancelToken) this.sourceCancelToken = this.cancelToken.source();
      this.isTabActive = true;
      this.loadStoriesOnFilters();
    }
  }

  async onScrollLoadData() {
    const { loading } = this.props;
    const { initialStoriesLoaded, pageToken } = this.state;
    if (this.newStoriesFetching || loading || !initialStoriesLoaded) return;

    if (pageToken !== 'EOD' && !isNull(pageToken)) {
      this.newStoriesFetching = true;
      await this.loadStoriesOnFilters(true);
      this.newStoriesFetching = false;
    }
  }

  static getDerivedStateFromProps(props, state) {
    const nextState = {};

    if (props.initialStoriesLoaded && !state.initialStoriesLoaded) {
      nextState.initialStoriesLoaded = true;
    }

    return nextState;
  }

  onStoryHide(id) {
    const { searchActive, hideSearchStory, hidePopularStory } = this.props;

    const action = searchActive ? hideSearchStory : hidePopularStory;
    action(id);
  }

  getParams(filters) {
    const { accessLevels } = this.props;
    const hasSilverAccess = accessLevels.includes(SILVER);
    const params = pickParams(
      { ...filters },
      'categories', 'languages',
      'all_languages', 'asset_classes', 'sectors',
      'industries', 'subindustries', 'startup_categories',
      'events', 'twitter_following_only', 'with_links', 'query',
      'source', 'translate_to', 'page_token',
      'selected_time_filter', 'blacklisted_domain_ids', 'whitelisted_domain_ids', 'domains_filtering_mode',
      ...filterSelectedTimeFilter(filters.selected_time_filter),
      ...filterSelectedSentimentForStories(filters, hasSilverAccess),
      'isRefresh',
    );

    if (filters.all_languages) delete params.languages;

    params.all_languages = Boolean(filters.all_languages);
    params.fold_similar_stories = filters.group_similar_stories;
    if (params.query) params.query = getFormattedQuery(params.query);

    return params;
  }

  getQuery() {
    const {
      items, activeSearchType, searchQueryFromUrl, investorPage,
    } = this.props;
    let urlQuery = getParamValueFromUrl('q');

    if (investorPage && searchQueryFromUrl) {
      return searchQueryFromUrl;
    }
    if (urlQuery) {
      urlQuery = decodeURI(urlQuery).trim();
    }

    if (activeSearchType === ADVANCED_SEARCH) {
      return buildAdvancedSearchQuery(items);
    }

    return items.length ? getTopicGroup(...items) : urlQuery;
  }

  handleRefreshEvent() {
    this.stopTimers();

    this.setState({
      pageToken: null,
      newStoriesAvailable: false,
    });

    this.scrollToStart(false);

    this.loadStoriesOnFilters(false, true);
  }

  handleCloseWarning() {
    this.setState({
      isWarningMessageShow: false,
    });
  }

  async loadStoriesOnFilters(nextPage = false, isRefresh) {
    const { actions, storyFilters, earliestSearchDate } = this.props;
    const { pageToken } = this.state;

    if (!storyFilters.conflictsChecked) return;

    this.stopTimers();
    const filters = { ...storyFilters };
    const query = this.getQuery();

    if (!query) return;

    if (!nextPage) {
      if (this.sourceCancelToken) this.sourceCancelToken.cancel();

      this.cancelToken = axios.CancelToken;
      this.sourceCancelToken = this.cancelToken.source();
    }

    const params = {
      ...this.getParams(filters),
      ...(nextPage && pageToken && {
        page_token: pageToken,
      }),
      cancelToken: this.sourceCancelToken.token,
      ...(isRefresh && { isRefresh }),
    };

    const { cancelled } = await actions.searchStories({
      query, params, nextPage, client: '', earliestSearchDate,
    });

    if (cancelled) return;

    if (filters.refresh_rate !== 'never' && Number(filters.refresh_rate) && this.isTabActive) {
      const { isFilterFixed } = this.state;
      if (nextPage || isFilterFixed) {
        if (!this.storyUpdateTimer) {
          this.storyUpdateTimer = setTimeout(this.checkForUpdate, filters.refresh_rate);
        }
      } else {
        clearTimeout(this.storyUpdateTimer);
        this.storyUpdateTimer = 0;
        this.searchResultTimer = setTimeout(this.loadStoriesOnFilters, filters.refresh_rate);
      }
    }
  }

  scrollToStart(fetchNewStories = true) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    this.setState({
      newStoriesAvailable: false,
    });

    if (!fetchNewStories) return;
    this.loadStoriesOnFilters();
  }

  async checkForUpdate() {
    const {
      storyFilters,
      stories,
    } = this.props;

    const params = this.getParams(storyFilters);

    const query = this.getQuery();

    const storiesRequest = storiesLoadAPIData(
      query,
      { ...params, cancelToken: this.sourceCancelToken.token },
    );
    const { stories: newStories, cancelled } = await storiesRequest;

    if (cancelled) return;

    const nonDuplicateStoriesIds = new Set([
      ...stories.slice(0, STORIES_LIMIT).map(({ id }) => id),
      ...newStories.map(({ id }) => id),
    ]);

    if (nonDuplicateStoriesIds.size > STORIES_LIMIT) {
      this.setState({
        newStoriesAvailable: true,
      });
    } else {
      const { isFilterFixed } = this.state;
      if (isFilterFixed) {
        this.storyUpdateTimer = setTimeout(this.checkForUpdate, storyFilters.refresh_rate);
      } else {
        clearTimeout(this.storyUpdateTimer);
        this.storyUpdateTimer = 0;
        this.searchResultTimer = setTimeout(this.loadStoriesOnFilters, storyFilters.refresh_rate);
      }
    }
  }

  stopTimers() {
    clearTimeout(this.searchResultTimer);
  }

  render() {
    const {
      searchActive,
      loading,
      storyFilters,
      actions,
      stories,
      isMobile,
      piwikEnabled,
      summaryRequestMethod,
      activeSearchType,
      width,
      investorPage,
      isNewPrivateCompanies,
    } = this.props;
    const {
      initialStoriesLoaded,
      newStoriesAvailable,
      seeMoreStoriesTop,
      position,
      pageToken,
      isWarningMessageShow,
    } = this.state;

    const isCompactView = isMobileOnly ? (
      storyFilters.user_newsfeed_compactview
    ) : (
      storyFilters.user_newsfeed_compactview_web
    );
    const userNewsfeedView = isMobileOnly ? (
      storyFilters.user_newsfeed_view
    ) : (
      storyFilters.user_newsfeed_view_web
    );
    const isTilesView = userNewsfeedView === 'tiles' && !isCompactView;
    const allowMasonry = isTilesView && width > 767;
    const StoriesWrapper = allowMasonry ? Masonry : 'div';
    const storiesWrapperProps = {
      className: cx({
        tiles_wrapper: isTilesView,
      }),
      ...(allowMasonry && {
        disableImagesLoaded: true,
        options: {
          gutter: 10,
          percentPosition: true,
          resize: false,
          isResizeBound: false,
          ...(!isTilesView && { transitionDuration: 0 }),
        },
        ref: (c) => { this.masonry = this.masonry || c.masonry; },
      }),
    };

    return (
      <>
        <div className={cx('search-result', { tiles: isTilesView })} ref={this.storiesRef}>
          <NewStoriesButton
            className={cx('new-stories-btn', { visible: newStoriesAvailable })}
            onClick={this.scrollToStart}
            style={{
              position,
              top: `${seeMoreStoriesTop}px`,
            }}
          />
          {(isMobile && (activeSearchType === ADVANCED_SEARCH)) && (
            <div className={Styles['search-result-add-query']}>
              <div className={Styles['search-result-plus']}>+</div>
              Add this query to a watchlist
            </div>
          )}
          {!investorPage && <h2>Stories</h2>}
          <article>
            <div>
              {!isMobile && (
                <OpenFiltersButton
                  isSearch
                  refreshEvent={this.handleRefreshEvent}
                  onSimplifiedViewTabsClick
                  isSimpleLayout={false}
                  isAdvancedLayoutDisabled
                  className={cx('search_filters')}
                />
              )}
            </div>
            <div>
              {isMobile && (
                <OpenFiltersButton
                  isSearch
                  refreshEvent={this.handleRefreshEvent}
                  onSimplifiedViewTabsClick
                  isSimpleLayout={false}
                  isAdvancedLayoutDisabled
                />
              )}
              <PiwikContext.Consumer>
                {(value) => (
                  <StoryFilters
                    isSearch
                    hasBigScreenDesign
                    piwikEnabled={value.piwikEnabled}
                    isSimpleLayout
                  />
                )}
              </PiwikContext.Consumer>
            </div>

            {isNewPrivateCompanies && isWarningMessageShow && (
              <div className={Styles['process-message']}>
                <div className={Styles['process-message-warning']}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                </div>
                <div className={Styles['process-message-text']}>
                  Stories for this topic are still being processed. Accuracy may vary.
                </div>
                <div
                  className={Styles['process-message-close']}
                  onClick={this.handleCloseWarning}
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                  />
                </div>
              </div>
            )}

            <Summarisation
              storiesEndpoint={summaryRequestMethod}
              isTilesView={isTilesView}
              isHidden={initialStoriesLoaded && !stories.length}
              className={cx('summarisation')}
              hasBigScreenDesign
            />
            <div
              className={cx('stories', {
                'stories-loading': initialStoriesLoaded && loading && !this.newStoriesFetching,
                'stories-tiles': isTilesView,
              })}
            >
              {(!initialStoriesLoaded) ? (
                <Preloader
                  loading
                  static
                  centered
                />
              ) : (
                <StoriesWrapper {...storiesWrapperProps}>
                  {stories.map((story, index) => (
                    <StoryCard
                      key={story.id}
                      expandFilter={storyFilters.expand_stories}
                      userNewsfeedViewImages={storyFilters.user_newsfeed_view_images}
                      userNewsfeedViewDescription={storyFilters.user_newsfeed_view_description}
                      userNewsfeedView={storyFilters.user_newsfeed_view}
                      tilesView={storyFilters.tiles_view}
                      showTranslation={storyFilters.translate_all}
                      type={story.type}
                      story={story}
                      onHide={this.onStoryHide}
                      onFreezeStories={actions.freezeStories}
                      onLikeChanged={actions.updateSearchStoriesInfo}
                      onBookmarkClick={actions.updateSearchStoriesBookmark}
                      piwikEnabled={piwikEnabled}
                      loading={loading}
                      masonryObj={this.masonry}
                      index={index}
                      hasBigScreenDesign
                    />
                  ))}
                </StoriesWrapper>
              )}
              {loading && this.newStoriesFetching && (
                <Preloader
                  loading
                  static
                  centered
                />
              )}
              {(
                stories.length < 20
                && pageToken === 'EOD'
                && initialStoriesLoaded
                && !(loading && searchActive)
                && !(!stories.length && loading))
                && (
                  <PiwikContext.Consumer>
                    {(value) => (
                      <RecommendedStories
                        searchPage
                        query={this.getQuery()}
                        storiesLength={stories.length}
                        loadingStories={loading}
                        piwikEnabled={value.piwikEnabled}
                        isAdvancedView
                        oldStories={stories}
                      />
                    )}
                  </PiwikContext.Consumer>
                )}
              {stories.length >= 30 && pageToken === 'EOD' && (
                <StoryCaughtUp hideButton />
              )}
            </div>
          </article>
        </div>
      </>
    );
  }
}

SearchResult.defaultProps = {
  stories: [],
};

SearchResult.propTypes = {
  stories: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string,
    fontSize: PropTypes.number,
  })),
};

function mapStateToProps({
  searchReducers,
  storyFilters,
  stories,
  common,
  promobar,
  subscriptions,
}, props) {
  const searchQuery = searchReducers.query || props.searchQueryFromUrl;
  const items = searchReducers[searchTypeListMapping[searchReducers.activeSearchType]];
  const searchActive = Boolean(items.length || searchQuery);

  return {
    loading: searchActive ? searchReducers.searchInProgress : stories.loading,
    freezeStories: stories.freeze_stories,
    stories: StoriesSvc.filterStories(searchReducers.stories, storyFilters),
    items,
    activeSearchType: searchReducers.activeSearchType,
    query: searchQuery,
    topic: stories.id,
    initialStoriesLoaded: searchReducers.initialStoriesLoaded,
    storyFilters,
    searchActive,
    nextPageToken: searchReducers.nextPageToken,
    scrollUpPressed: common.scrollUpPressed,
    width: common.width,
    promobarHeight: promobar.height,
    permissions: subscriptions.permissions,
    earliestSearchDate: subscriptions.permissions.earliest_search_date,
    accessLevels: subscriptions.permissions.access_levels,
    summaryRequestMethod: searchReducers.summaryRequestMethod,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      ...storyActions,
      ...searchActions,
      ...commonActions,
      ...preferencesActions,
    }, dispatch),
    hidePopularStory: (id) => dispatch(storyActions.hideStory(id)),
    hideSearchStory: (id) => dispatch(searchActions.hideStory(id)),
  };
}

export default withComponentName(
  connect(mapStateToProps, mapDispatchToProps)(SearchResult),
);
